import { apiClient } from './client';
import { MarketplaceSupplier } from './marketplace';

export interface AdminSupplier extends MarketplaceSupplier {
    company?: string; // Align with Prisma 'company'
    apiKey?: string;
    apiSecret?: string;
    oauthClientSecret?: string;
    oauthTokenUrl?: string;
    oauthScopes: string[];
    oauthClientId?: string;
    oauthAuthorizeUrl?: string;
    address?: string;
    city?: string;
    country?: string;
    postalCode?: string;
    notes?: string;
    tags: string[];
    connectedStores: number;
    createdAt: string;
    updatedAt: string;
}

export interface ServerMetricCurrent {
    recordedAt: string;
    requestCount: number;
    avgResponseMs: number;
    p95ResponseMs: number;
    error4xx: number;
    error5xx: number;
    cacheHits: number;
    cacheMisses: number;
    cacheHitRatio: number;
    memoryUsedMb: number;
    cpuPercent: number;
    activeSseConnections: number;
    slowQueriesCount: number;
    statusCodes: Array<{ statusCode: number; count: number }>;
    topRoutes: Array<{ route: string; requestCount: number; avgResponseMs: number; error5xx: number }>;
    topErrorRoutes: Array<{ route: string; error5xx: number; requestCount: number }>;
}

export interface ServerMetricHistoryRow {
    id: string;
    recordedAt: string;
    requestCount: number;
    avgResponseMs: number;
    p95ResponseMs: number;
    error4xx: number;
    error5xx: number;
    cacheHits: number;
    cacheMisses: number;
    cacheHitRatio: number;
    memoryUsedMb: number;
    cpuPercent: number;
    activeSseConnections: number;
    slowQueriesCount: number;
}

export const adminApi = {
    // Supplier Management
    async getSuppliers(params?: {
        search?: string;
        isMarketplace?: boolean;
        isVerified?: boolean;
        integrationType?: string;
        page?: number;
        limit?: number;
    }): Promise<{ suppliers: AdminSupplier[]; pagination: any }> {
        const queryParams = new URLSearchParams();
        if (params?.search) queryParams.append('search', params.search);
        if (params?.isMarketplace !== undefined) queryParams.append('isMarketplace', params.isMarketplace.toString());
        if (params?.isVerified !== undefined) queryParams.append('isVerified', params.isVerified.toString());
        if (params?.integrationType) queryParams.append('integrationType', params.integrationType);
        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.limit) queryParams.append('limit', params.limit.toString());

        return apiClient.get(`/admin/suppliers?${queryParams.toString()}`);
    },

    async getSupplier(id: string): Promise<AdminSupplier> {
        return apiClient.get(`/admin/suppliers/${id}`);
    },

    async createSupplier(data: Partial<AdminSupplier>): Promise<AdminSupplier> {
        return apiClient.post('/admin/suppliers', data);
    },

    async updateSupplier(id: string, data: Partial<AdminSupplier>): Promise<AdminSupplier> {
        return apiClient.put(`/admin/suppliers/${id}`, data);
    },

    async deleteSupplier(id: string): Promise<{ message: string }> {
        return apiClient.delete(`/admin/suppliers/${id}`);
    },

    async generateSupplierApiKeys(id: string): Promise<{ apiKey: string; apiSecret: string }> {
        return apiClient.post(`/admin/suppliers/${id}/generate-keys`, {});
    },

    async approveSupplier(id: string): Promise<{ supplier: AdminSupplier; credentialsSent: boolean }> {
        return apiClient.post(`/admin/suppliers/${id}/approve`, {});
    },

    async getServerMetricsCurrent(): Promise<ServerMetricCurrent> {
        return apiClient.get('/analytics/admin/metrics/current');
    },

    async getServerMetricsHistory(hours = 24): Promise<ServerMetricHistoryRow[]> {
        return apiClient.get(`/analytics/admin/metrics/history?hours=${hours}`);
    },
};
